import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp, Check } from 'lucide-react';

const CustomDropdown = ({
    placeholder,
    iconLeft,
    options = [],
    selected = [],
    setSelected,
    width = '100%',
    hasMore = false,
    onLoadMore,
    onSearch,
    isTelaEstado
}) => {
    const [open, setOpen] = useState(false);
    const listRef = useRef(null);
    const containerRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');

    const [localSelected, setLocalSelected] = useState(selected || []);

    useEffect(() => {
        setLocalSelected(selected || []);
    }, [selected]);

    // Fecha ao clicar fora
    useEffect(() => {
        function handleClickOutside(event) {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false);
            }
        }
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open]);

    // helpers
    const getValue = (opt) => (opt && typeof opt === 'object') ? opt.value : opt;
    const getLabel = (opt) => (opt && typeof opt === 'object') ? (opt.label || String(opt.value || opt)) : String(opt);
    const getParent = (opt) => (opt && typeof opt === 'object') ? opt.parent : null;

    const isTodosOption = (opt) => {
        if (!opt) return false;
        const label = opt.label || '';
        const val = getValue(opt);
        return typeof val === 'string' && label.includes('Todos os') ;
    };

    const isSelected = (option) =>
        localSelected.some(sel => getValue(sel) === getValue(option));

    // quando o usuário adiciona/remover uma opção:
    const toggleOption = (option) => {
        const val = getValue(option);

        if (isSelected(option)) {
            const novo = localSelected.filter(item => getValue(item) !== val);
            setLocalSelected(novo);
            setSelected && setSelected(novo);
            return;
        }

        if (isTodosOption(option)) {
            const uf = val;
            const novo = [
                ...localSelected.filter(item => getParent(item) !== uf),
                option
            ];
            setLocalSelected(novo);
            setSelected && setSelected(novo);
            return;
        }

        const parent = getParent(option);
        let novo = localSelected;
        if (parent) {
            const todosSelecionado = localSelected.find(
                sel => getValue(sel) === parent && isTodosOption(sel)
            );
            if (todosSelecionado) {
                novo = localSelected.filter(sel => getValue(sel) !== parent);
            }
        }
        novo = [...novo, option];
        setLocalSelected(novo);
        setSelected && setSelected(novo);
    };

    // Infinite scroll
    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop <= clientHeight + 10 && hasMore && onLoadMore) {
            onLoadMore();
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (onSearch) onSearch(value);
    };
    
    const ufsTodosSet = new Set(
        localSelected
            .filter(sel => isTodosOption(sel))
            .map(sel => getValue(sel))
    );

    let filteredOptions = options;
    if (!isTelaEstado && ufsTodosSet.size > 0) {
        filteredOptions = options.filter(opt => {
            
            if (ufsTodosSet.has("BR")) {
                hasMore = false;    
                return getValue(opt) === "BR";
            }
            
            const parent = opt.parent;
            if (parent && ufsTodosSet.has(parent)) {
                return false;
            }
            return true;
        });
    }
    // Exibe os selecionados no header
    const selectedLabels = localSelected.map(getLabel).join(', ');

    return (
        <div ref={containerRef} style={{ width, position: 'relative', marginBottom: 16 }}>
            <div
                className="custom-dropdown-header"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #ccc',
                    borderRadius: 6,
                    padding: '10px 14px',
                    cursor: 'pointer',
                    background: '#fff',
                    position: 'relative',
                    gap: 8,
                }}
                onClick={() => setOpen(!open)}
            >
                {iconLeft && (
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                        {iconLeft}
                    </span>
                )}
                <span style={{ color: localSelected.length ? '#222' : '#3C3C3C', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                    {localSelected.length ? selectedLabels : placeholder}
                </span>
                <span style={{ display: 'flex', alignItems: 'center' }}>
                    {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </span>
            </div>

            {open && (
                <div
                    className="custom-dropdown-list"
                    style={{
                        position: 'absolute',
                        top: '110%',
                        left: 0,
                        width: '100%',
                        background: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: 6,
                        zIndex: 10,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        maxHeight: 200,
                        overflowY: 'auto',
                    }}
                    onScroll={handleScroll}
                    ref={listRef}
                >
                    <div style={{ padding: 8 }}>
                        <input
                            type="text"
                            placeholder="Buscar..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            style={{
                                width: '100%',
                                padding: 6,
                                borderRadius: 4,
                                border: '1px solid #ccc',
                                marginBottom: 8,
                                fontSize: 14
                            }}
                        />
                    </div>

                    {filteredOptions.map(option => (
                        <div
                            key={getValue(option)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '8px 14px',
                                cursor: 'pointer',
                                background: isSelected(option) ? '#f0f4ff' : '#fff'
                            }}
                            onClick={() => toggleOption(option)}
                        >
                            <input
                                type="checkbox"
                                checked={isSelected(option)}
                                readOnly
                                style={{ marginRight: 8 }}
                            />
                            <span style={{ flex: 1 }}>{getLabel(option)}</span>
                            {isSelected(option) && <Check size={16} color="#1976d2" />}
                        </div>
                    ))}

                    {hasMore && (
                        <div style={{ padding: 10, textAlign: 'center', color: '#888' }}>
                            Carregando mais...
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CustomDropdown;
